import React, { useState, forwardRef } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import { Logo, LogoIcon, SearchInput } from '../index';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const sidebarVariants = cva(
  "bg-white dark:bg-dark-100 border-r border-gray-200 dark:border-dark-300 flex flex-col transition-colors duration-200 relative",
  {
    variants: {
      size: {
        default: "w-64 h-full",
        collapsed: "w-16 h-full",
        mobile: "fixed inset-y-0 left-0 z-50 w-64 h-full transform transition-transform duration-300 ease-in-out",
        "mobile-hidden": "fixed inset-y-0 left-0 z-50 w-64 h-full transform -translate-x-full transition-transform duration-300 ease-in-out",
      },
    },
    defaultVariants: {
      size: "default",
    },
  }
);

const navItemVariants = cva(
  "flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 cursor-pointer",
  {
    variants: {
      variant: {
        default: "text-gray-600 dark:text-dark-600 hover:text-gray-900 dark:hover:text-dark-900 hover:bg-gray-100 dark:hover:bg-dark-200",
        active: "text-primary-700 dark:text-accent-500 bg-primary-50 dark:bg-accent-500/10",
        collapsed: "justify-center px-2 text-gray-600 dark:text-dark-600 hover:text-gray-900 dark:hover:text-dark-900 hover:bg-gray-100 dark:hover:bg-dark-200",
        "collapsed-active": "justify-center px-2 text-primary-700 dark:text-accent-500 bg-primary-50 dark:bg-accent-500/10",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export const Sidebar = React.forwardRef(({
  className,
  collapsed = false,
  activeItem = "dashboard",
  navigationItems = [],
  user,
  onNavigate,
  isMobile = false,
  mobileMenuOpen = false,
  onMobileMenuToggle,
  ...props
}, ref) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { logout } = useAuth();
  const navigate = useNavigate();

  const defaultNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'fa-solid fa-house' },
    { id: 'tasks', label: 'Tasks', icon: 'fa-solid fa-list-check', badge: 8 },
    { id: 'calendar', label: 'Calendar', icon: 'fa-solid fa-calendar' },
    { id: 'notes', label: 'Notes', icon: 'fa-solid fa-note-sticky' },
    { 
      id: 'projects', 
      label: 'Projects', 
      icon: 'fa-solid fa-folder', 
      children: [
        { id: 'marketing', label: 'Marketing' },
        { id: 'development', label: 'Development' }
      ]
    },
  ];

  const navItems = navigationItems.length > 0 ? navigationItems : defaultNavItems;

  const handleItemClick = (item) => {
    onNavigate?.(item);
    // Close mobile menu when item is clicked
    if (isMobile && mobileMenuOpen) {
      onMobileMenuToggle?.(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Still navigate to login on error as a fallback
      navigate('/login');
    }
  };

  // Determine which variant to use
  const getSidebarSize = () => {
    if (isMobile) {
      return mobileMenuOpen ? 'mobile' : 'mobile-hidden';
    }
    return collapsed ? 'collapsed' : 'default';
  };

  const showFullContent = !collapsed || (isMobile && mobileMenuOpen);
  const showCollapsedContent = collapsed && !isMobile;

  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && mobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => onMobileMenuToggle?.(false)}
        />
      )}

      <div
        ref={ref}
        className={cn(sidebarVariants({ size: getSidebarSize() }), className)}
        {...props}
      >
        {/* Header */}
        <div className="flex-shrink-0 p-4 border-b border-gray-200 dark:border-dark-300">
          <div className="flex items-center justify-between">
            {showCollapsedContent ? (
              <LogoIcon 
                size="default"
                className="mx-auto"
              />
            ) : (
              <Logo 
                size="default" 
                layout="horizontal"
              />
            )}
            
            {/* Mobile Close Button */}
            {isMobile && mobileMenuOpen && (
              <button 
                onClick={() => onMobileMenuToggle?.(false)}
                className="p-2 text-gray-400 dark:text-dark-500 hover:text-gray-600 dark:hover:text-dark-700 ml-2"
              >
                <i className="fa-solid fa-times" />
              </button>
            )}
          </div>
        </div>

        {/* Search */}
        {showFullContent && (
          <div className="flex-shrink-0 p-4">
            <SearchInput
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="sm"
              className="bg-gray-50 dark:bg-dark-200 border-gray-200 dark:border-dark-300"
            />
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-4 py-2 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <div key={item.id}>
              <div
                className={cn(
                  navItemVariants({ 
                    variant: activeItem === item.id 
                      ? (showCollapsedContent ? 'collapsed-active' : 'active')
                      : (showCollapsedContent ? 'collapsed' : 'default')
                  })
                )}
                onClick={() => handleItemClick(item)}
                title={showCollapsedContent ? item.label : undefined}
              >
                <i className={cn(item.icon, showCollapsedContent ? "text-base" : "mr-3 text-sm")} />
                {showFullContent && (
                  <>
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.badge && (
                      <span className="ml-2 bg-primary-100 dark:bg-accent-500/20 text-primary-700 dark:text-accent-400 text-xs font-medium px-2 py-0.5 rounded-full">
                        {item.badge}
                      </span>
                    )}
                    {item.children && (
                      <i className="fa-solid fa-chevron-down ml-2 text-xs" />
                    )}
                  </>
                )}
              </div>
              
              {/* Sub-items */}
              {showFullContent && item.children && activeItem === item.id && (
                <div className="ml-6 mt-1 space-y-1">
                  {item.children.map((child) => (
                    <div
                      key={child.id}
                      className="flex items-center px-3 py-1.5 rounded-md text-sm text-gray-600 dark:text-dark-600 hover:text-gray-900 dark:hover:text-dark-900 hover:bg-gray-50 dark:hover:bg-dark-200 cursor-pointer"
                      onClick={() => handleItemClick(child)}
                    >
                      <div className="w-2 h-2 bg-gray-400 dark:bg-dark-500 rounded-full mr-3 flex-shrink-0" />
                      <span className="truncate">{child.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* User Profile */}
        <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-dark-300">
          <div className={cn(
            "flex items-center",
            showCollapsedContent ? "justify-center" : "space-x-3"
          )}>
            {user?.avatar ? (
              <img
                src={user.avatar}
                alt={user.name}
                className="w-8 h-8 rounded-full flex-shrink-0"
                title={showCollapsedContent ? user.name : undefined}
              />
            ) : (
              <div 
                className="w-8 h-8 rounded-full bg-primary-100 dark:bg-accent-500/20 flex items-center justify-center flex-shrink-0"
                title={showCollapsedContent ? (user?.name || 'User') : undefined}
              >
                <span className="text-sm font-medium text-primary-700 dark:text-accent-400">
                  {user?.name?.charAt(0) || 'U'}
                </span>
              </div>
            )}
            
            {showFullContent && (
              <>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-dark-900 truncate">
                    {user?.name || 'John Smith'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-dark-600 truncate">
                    {user?.email || 'john@example.com'}
                  </p>
                </div>
                
                <button 
                  className="p-1 text-gray-400 dark:text-dark-500 hover:text-red-600 dark:hover:text-red-400 flex-shrink-0 transition-colors duration-200"
                  onClick={handleLogout}
                  title="Logout"
                >
                  <i className="fa-solid fa-sign-out-alt" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
});

Sidebar.displayName = "Sidebar";

// Responsive Sidebar Wrapper
export const ResponsiveSidebar = React.forwardRef(({
  className,
  collapsed = false,
  ...props
}, ref) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <>
      {/* Desktop/Tablet Sidebar */}
      <div className="hidden md:flex">
        <Sidebar
          ref={ref}
          collapsed={collapsed}
          className={className}
          {...props}
        />
      </div>

      {/* Mobile Sidebar */}
      <div className="md:hidden">
        <Sidebar
          isMobile={true}
          mobileMenuOpen={mobileMenuOpen}
          onMobileMenuToggle={setMobileMenuOpen}
          className={className}
          {...props}
        />
      </div>
    </>
  );
});

ResponsiveSidebar.displayName = "ResponsiveSidebar";

// Mobile Header Component for responsive navigation
export const MobileHeader = React.forwardRef(({
  className,
  onMenuToggle,
  title = "Dashboard",
  user,
  ...props
}, ref) => {
  return (
    <header
      ref={ref}
      className={cn(
        "md:hidden bg-white dark:bg-dark-100 border-b border-gray-200 dark:border-dark-300 px-4 py-3 flex items-center justify-between transition-colors duration-200",
        className
      )}
      {...props}
    >
      {/* Left: Menu Button + Title */}
      <div className="flex items-center space-x-3">
        <button
          onClick={onMenuToggle}
          className="p-2 text-gray-600 dark:text-dark-600 hover:text-gray-900 dark:hover:text-dark-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 dark:focus:ring-accent-500 rounded-md"
        >
          <i className="fa-solid fa-bars text-lg" />
        </button>
        <h1 className="text-lg font-semibold text-gray-900 dark:text-dark-900 truncate">{title}</h1>
      </div>

      {/* Right: User Avatar */}
      <div className="flex items-center flex-shrink-0">
        {user?.avatar ? (
          <img
            src={user.avatar}
            alt={user.name}
            className="w-8 h-8 rounded-full"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-accent-500/20 flex items-center justify-center">
            <span className="text-sm font-medium text-primary-700 dark:text-accent-400">
              {user?.name?.charAt(0) || 'U'}
            </span>
          </div>
        )}
      </div>
    </header>
  );
});

MobileHeader.displayName = "MobileHeader"; 